import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Menu, Tag, Popover, Tooltip } from 'antd';
import {
  enumTranslator,
  isEmpty,
  translatorVendingScheduleState,
} from '@/utils';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { useSelector } from 'react-redux';
import { VehicleScheduleState, VehicleTag } from '@/redux/type';
import { analyzeLog, clstagStr, convertSpeedMsToKmh } from '@/utils/util';
import AttentionIcon from '@/components/AttentionIcon';
import { CLSTAG, CockpitMode } from '@/utils/constant';
import { Method } from '@/fetch/core/util';
const { Option } = Select;
const { SubMenu } = Menu;
type MenuProps = {
  menuData: any;
  activeMode: 'boardMode' | 'mapMode';
  onMenuOpenChange: AnyFunc;
  onPositionBtnClick: AnyFunc;
  openKeys: string[];
  setOpenKeys: AnyFunc;
};

type MenuItem = {
  title: string | HTMLElement;
  titleText: string;
  level: number;
  parentKey: string;
  id: number;
  key: string;
  children: any[];
  vehicleState: string;
  abnormal: number;
  total: number;
  onlineNum: number;
  attentionState: boolean;
  number?: string;
};

type MenuIndexItem = {
  key: string;
  titleText: string;
  parentKey: string;
};

const createMenuIndex = (data: MenuItem[], result: MenuIndexItem[] = []) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    result.push({
      key: node.key,
      titleText: node.titleText,
      parentKey: node.parentKey,
    });
    if (node.children) {
      createMenuIndex(node.children, result);
    }
  }
  return result;
};

const formatOptionsData = (data: any, result: any[] = []) => {
  for (const item of data) {
    const { titleText, parentKey, key, children, number } = item;
    if (item.parentKey) {
      result.push({
        text: titleText,
        searchText: number?.trim() ? `${titleText} ${number}` : titleText,
        value: key,
        parentKey,
      });
    }
    if (!isEmpty(children)) {
      formatOptionsData(children, result);
    }
  }

  return result;
};

const LeftMenu = (props: MenuProps) => {
  const {
    menuData,
    onMenuOpenChange,
    activeMode,
    onPositionBtnClick,
    openKeys,
    setOpenKeys,
  } = props;
  const { cockpitMode } = useSelector((state: any) => state.cockpitInfo);
  const menuIndex = useMemo(() => createMenuIndex(menuData), [menuData]);
  const selectOptions = useMemo(() => formatOptionsData(menuData), [menuData]);
  const parentKeyByMenuKey = useMemo(
    () => new Map(menuIndex.map((item) => [item.key, item.parentKey])),
    [menuIndex],
  );
  const [searchValue, setSearchValue] = useState('');
  const [popoverData, setPopoverData] = useState<any>({});
  const [activePopoverKey, setActivePopoverKey] = useState('');
  const activePopoverKeyRef = useRef('');

  /**
   *
   * @param {string[]} keys 菜单展开节点的keys
   */
  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    // 城市或站点展开
    if (latestOpenKey) {
      onMenuOpenChange(latestOpenKey.split('-'));
    }
    setOpenKeys(keys);
  };

  /**
   *
   * @param {string} value 筛选框内容
   */
  const onChange = (value: string) => {
    if (value === undefined) {
      // 删除时返回的时空串
      value = '';
    }

    if (!value) {
      setSearchValue(value);
      setOpenKeys([]);
      return;
    }
    const matchedParentKeys = new Set<string>();
    menuIndex.forEach((item) => {
      if (!item.titleText.includes(value)) {
        return;
      }

      let parentKey = item.parentKey;
      while (parentKey) {
        matchedParentKeys.add(parentKey);
        parentKey = parentKeyByMenuKey.get(parentKey) || '';
      }
    });

    setSearchValue(value);
    setOpenKeys(Array.from(matchedParentKeys));
  };

  const onMenuItemClick = ({ item, key, keyPath, domEvent }: any) => {
    const pathList = key.split('-');
    const vehicleName = pathList[pathList.length - 1];
    const url = '/app/single?license=' + vehicleName;
    window.open(url, '_blank');
  };

  const onVisibleChange = (visible: boolean, data: any) => {
    if (!visible) {
      if (activePopoverKeyRef.current === data.key) {
        activePopoverKeyRef.current = '';
        setActivePopoverKey('');
      }
      return;
    }

    activePopoverKeyRef.current = data.key;
    setActivePopoverKey(data.key);
    request({
      path: '/monitor/web/realtime/navibar/vehicle',
      method: Method.POST,
      body: {
        vehicleName: data.titleText,
      },
    }).then((res: any) => {
      if (
        res.code === HttpStatusCode.Success &&
        activePopoverKeyRef.current === data.key
      ) {
        setPopoverData(res.data);
      }
    });
    analyzeLog('menuItem', `{"vehicleName":${data.titleText}}`);
  };

  useEffect(() => {
    setTimeout(() => {
      const activeMenuNode = document.querySelector('.active-menu');
      if (activeMenuNode) {
        activeMenuNode.scrollIntoView();
      }
    }, 1000);
  }, [searchValue]);

  const popoverContent = useMemo(() => {
    const scheduleLabel =
      popoverData.businessType === 'VENDING'
        ? translatorVendingScheduleState(popoverData.scheduleState)
        : popoverData.taskType === 'DROPOFF' &&
            popoverData.scheduleState === 'DELIVERY'
          ? VehicleScheduleState.DELIVERY
          : popoverData.taskType === 'DROPOFF' &&
              popoverData.scheduleState === 'ALREADY'
            ? VehicleScheduleState.ALREADY
            : enumTranslator('SCHEDULE_STATE_ENUM', popoverData.scheduleState);
    return (
      <ul className='popover-list'>
        <li>
          <span className='label'>
            {popoverData.businessType === 'VENDING'
              ? '售卖状态：'
              : '配送状态：'}
          </span>
          <span className='text'>{scheduleLabel || '--'}</span>
        </li>
        <li>
          <span className='label'>报警事件：</span>
          <span className='text'>
            {enumTranslator('ALARM_EVENT_ENUM', popoverData.alarmEvent) || '--'}
          </span>
        </li>
        <li>
          <span className='label'>速度：</span>
          <span className='text'>
            {convertSpeedMsToKmh(popoverData.speed, 1)}
          </span>
        </li>
        <li>
          <span className='label'>里程数：</span>
          <span className='text'>
            {(popoverData.mileage &&
              (popoverData.mileage / 1000).toFixed(2) + ' km') ||
              '--'}
          </span>
        </li>
      </ul>
    );
  }, [popoverData]);
  const getVehicleName: any = (item: any) => {
    if (item.level === 3) {
      return [item.titleText];
    } else {
      const result = [];
      for (let i = 0; i < item.children.length; i++) {
        const childNames = getVehicleName(item.children[i]);
        result.push(...childNames);
      }
      return result;
    }
  };

  const renderMenuList = (menuData: MenuItem[]) => {
    return menuData.map((item: MenuItem, index: number) => {
      const {
        key,
        children,
        id,
        level,
        abnormal,
        total,
        vehicleState,
        tagList,
        onlineNum,
        titleText,
        attentionState,
        number: vehicleNumber,
      } = item;
      const strIndex = item.titleText.indexOf(searchValue);
      const beforeStr = item.titleText.substr(0, strIndex);
      const afterStr = item.titleText.substr(strIndex + searchValue.length);
      const fontColorMap = new Map([
        ['OFFLINE', 'rgba(255,255,255,0.6)'],
        ['NORMAL', '#FFFFFF'],
        ['CONNECTION_LOST', '#98989D'],
        ['ABNORMAL', '#FF574D'],
      ]);

      const color = fontColorMap.get(vehicleState);
      const hasVehicleNumber = Boolean(vehicleNumber?.trim());
      const menuTitle =
        level === 1 ? (
          <i className='auto-ellipsis'>{titleText}</i>
        ) : level === 2 ? (
          <Tooltip title={titleText}>
            <i className='auto-ellipsis station-menu'>{titleText}</i>
          </Tooltip>
        ) : (
          titleText
        );
      const info =
        searchValue && strIndex > -1 ? (
          <span className='vehicle-name-text' style={{ flexGrow: 0.8 }}>
            {beforeStr}
            <span style={{ color: '#CCFF00' }} className='active-menu'>
              {searchValue}
              {tagList && tagList.includes(VehicleTag.BEIDOU) && (
                <i className='beidou-tag' />
              )}
            </span>
            {afterStr}
          </span>
        ) : (
          <span
            className={`vehicle-name-text ${vehicleState}`}
            style={{ color: color, flexGrow: 0.8 }}
          >
            {' '}
            {menuTitle}
            {tagList && tagList.includes(VehicleTag.BEIDOU) && (
              <i className='beidou-tag' />
            )}
          </span>
        );
      if (!isEmpty(children)) {
        return (
          <SubMenu
            className={`menu-item menu-level-${level}`}
            key={key}
            title={
              <span className='submenu-title group-ellipsis'>
                <span className='info-content'>
                  <span className='text-content'>{info}</span>
                  <span className='number-content'>
                    ({onlineNum}/{total})
                  </span>
                </span>
                {!!item.abnormal && <Tag className='menu-tag'>{abnormal}</Tag>}
                <span className='icon-content'>
                  {cockpitMode === CockpitMode.MONITOR && (
                    <AttentionIcon
                      attentionState={attentionState}
                      vehicleName={getVehicleName(item)}
                    />
                  )}
                </span>
              </span>
            }
          >
            {openKeys.includes(key) ? renderMenuList(children) : null}
          </SubMenu>
        );
      } else {
        return (
          <Menu.Item key={key}>
            <>
              <Popover
                placement='rightTop'
                overlayClassName='menu-popover'
                content={activePopoverKey === key ? popoverContent : null}
                trigger='hover'
                mouseEnterDelay={1}
                onOpenChange={(visible: boolean) =>
                  onVisibleChange(visible, item)
                }
              >
                <span className='vehicle-title'>
                  <span className='vehicle-name-content'>
                    {info}
                    {hasVehicleNumber && (
                      <Tag className='vehicle-number-tag'>{vehicleNumber}</Tag>
                    )}
                  </span>
                  {cockpitMode === CockpitMode.MONITOR && (
                    <AttentionIcon
                      attentionState={attentionState}
                      vehicleName={[titleText]}
                      addAttentionClstag={CLSTAG.MultiPage_AddAttention}
                      removeAttentionClstag={CLSTAG.MultiPage_RemoveAttention}
                    />
                  )}
                  {activeMode === 'mapMode' && (
                    <span
                      className='position-btn'
                      onClick={(e) => {
                        e.stopPropagation();
                        onPositionBtnClick(item.titleText);
                        analyzeLog('menuItem', `{"vehicleName":${item.titleText}}`);
                      }}
                    >
                      <i className='position-icon'></i>
                      定位
                    </span>
                  )}
                </span>
              </Popover>
            </>
          </Menu.Item>
        );
      }
    });
  };
  return (
    <>
      <div
        className='search-bar'
        clstag={clstagStr(CLSTAG.StationVehicleSearch)}
      >
        <Select
          allowClear
          showSearch
          style={{ width: 200 }}
          placeholder='站点/车号/编号'
          optionFilterProp='children'
          filterOption={true}
          onChange={onChange}
        >
          {selectOptions.map((item: any, index: number) => (
            <Option key={index} value={item.text}>
              {item.searchText}
            </Option>
          ))}
        </Select>
      </div>
      <div className='left-menu'>
        <Menu
          openKeys={openKeys}
          mode='inline'
          theme='dark'
          onOpenChange={onOpenChange}
          onClick={onMenuItemClick}
        >
          {renderMenuList(menuData)}
        </Menu>
      </div>
    </>
  );
};

export default React.memo(LeftMenu);
